const SERVER_URL = import.meta.env.VITE_API_BASE_URL || 'https://beemi-server-staging-s7ne.onrender.com';

interface ManifestConfig {
  id: string;
  name: string;
  version: string;
  entryPoint: string;
  beemi: {
    sdkVersion: string;
    core: { required: boolean };
    streams?: { required: boolean };
    'multiplayer-p2p'?: { required: boolean };
    user?: { required: boolean };
    'app-bridge'?: { required: boolean };
  };
}

// Fetch the recommended SDK version and emulator path from sdk-manifest.json
async function getRecommendedSDKInfo() {
  const manifestUrl = `${SERVER_URL}/sdk-manifest.json`;
  const response = await fetch(manifestUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch SDK manifest: ${response.status}`);
  }
  
  const sdkManifest = await response.json();
  const recommendedSdkKey = sdkManifest.recommended; // e.g., "beemi-sdk-2.1.5"
  const recommendedSdk = sdkManifest.sdks[recommendedSdkKey];
  
  if (!recommendedSdk) {
    throw new Error(`Recommended SDK not found: ${recommendedSdkKey}`);
  }
  
  // Get the emulator bridge path (first emulator file is the bridge)
  const emulatorBridgePath = recommendedSdk.emulatorFiles?.[0];
  if (!emulatorBridgePath) {
    throw new Error(`No emulator files found for ${recommendedSdkKey}`);
  }
  
  return {
    sdkVersion: recommendedSdk.version,
    sdkUrl: recommendedSdk.url,
    emulatorBridgeUrl: emulatorBridgePath
  };
}

export async function loadEmulatorBridge(
  iframe: HTMLIFrameElement, 
  manifest?: ManifestConfig
): Promise<boolean> {
  try {
    // Get recommended SDK and emulator info
    const { sdkVersion, sdkUrl, emulatorBridgeUrl } = await getRecommendedSDKInfo();
    
    console.log(`🔄 Loading recommended SDK v${sdkVersion} and emulator`);
    
    // Fetch emulator bridge
    const bridgeUrl = `${SERVER_URL}${emulatorBridgeUrl}`;
    console.log(`🔄 Fetching emulator bridge from: ${bridgeUrl}`);
    const bridgeResponse = await fetch(bridgeUrl);
    if (!bridgeResponse.ok) {
      throw new Error(`Failed to fetch emulator bridge: ${bridgeResponse.status}`);
    }
    
    const bridgeCode = await bridgeResponse.text();
    console.log(`📦 Bridge code fetched: ${bridgeCode.length} bytes`);
    
    // Fetch SDK in parent context (Studio) to avoid CORS issues with srcDoc iframe (null origin)
    // This matches React Native behavior: pre-fetch SDK, then inject into WebView
    const fullSdkUrl = `${SERVER_URL}${sdkUrl}`;
    console.log(`🔄 Pre-fetching SDK from parent context: ${fullSdkUrl}`);
    
    const sdkResponse = await fetch(fullSdkUrl);
    if (!sdkResponse.ok) {
      throw new Error(`Failed to fetch SDK: ${sdkResponse.status}`);
    }
    
    const sdkCode = await sdkResponse.text();
    console.log(`📦 SDK code fetched: ${sdkCode.length} bytes`);
    
    // RF1: Set configuration and manifest before injecting bridge
    const iframeWindow = iframe.contentWindow as any;
    
    // Set bridge configuration for Studio mode
    iframeWindow.BEEMI_CONFIG = {
      mode: 'direct',  // Inject SDK into current window, not child iframe
      serverUrl: SERVER_URL,
      iframeSelector: null,  // Not needed in direct mode
    };
    console.log('🔧 Bridge config set:', iframeWindow.BEEMI_CONFIG);
    
    // Inject manifest into iframe window if provided
    if (manifest) {
      // Override manifest's SDK version with recommended version
      const updatedManifest = {
        ...manifest,
        beemi: {
          ...manifest.beemi,
          sdkVersion: sdkVersion // Use recommended version
        }
      };
      iframeWindow.__BEEMI_MANIFEST__ = updatedManifest;
      console.log('📋 Manifest injected into window with recommended SDK:', updatedManifest);
    }
    
    // Inject pre-fetched SDK code (matches React Native's injectJavaScript approach)
    iframeWindow.__BEEMI_SDK_CODE__ = sdkCode;
    console.log('📦 Pre-fetched SDK code injected into iframe window');
    
    // Check if iframe document is ready
    if (!iframe.contentDocument) {
      throw new Error('Iframe content document not available');
    }
    
    console.log('📄 Iframe document ready, injecting bridge script...');
    
    // Create script element and inject bridge code
    // Inject at the END of the body (not in head) to preserve line numbers for HTML content
    const script = iframe.contentDocument.createElement('script');
    script.textContent = bridgeCode;
    
    // Inject at end of body if available, otherwise fall back to head
    if (iframe.contentDocument.body) {
      iframe.contentDocument.body.appendChild(script);
      console.log('✅ Bridge script injected at end of body');
    } else {
      iframe.contentDocument.head.appendChild(script);
      console.log('⚠️ Body not found, bridge script injected in head (fallback)');
    }
    
    console.log('✅ Bridge script injected, waiting for beemiDev...');
    
    // Wait for bridge to be ready
    await waitForBridgeReady(iframe, 10000); // Increased timeout to 10s
    
    console.log('✅ Emulator bridge loaded successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Failed to load emulator bridge:', error);
    throw error;
  }
}

async function waitForBridgeReady(
  iframe: HTMLIFrameElement, 
  timeout: number = 5000
): Promise<boolean> {
  const start = Date.now();
  let checkCount = 0;
  
  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(() => {
      checkCount++;
      const iframeWindow = iframe.contentWindow as any;
      
      // Debug: Log what we find every 10 checks (every second)
      if (checkCount % 10 === 0) {
        console.log(`🔍 Check #${checkCount}: beemiDev=${!!iframeWindow?.beemiDev}, beemi=${!!iframeWindow?.beemi}, window keys:`, 
          iframeWindow ? Object.keys(iframeWindow).filter(k => k.includes('beemi') || k.includes('BEEMI')) : 'no window');
      }
      
      if (iframeWindow?.beemiDev) {
        console.log(`✅ beemiDev found after ${checkCount} checks (${Date.now() - start}ms)`);
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - start > timeout) {
        console.error(`❌ Timeout after ${checkCount} checks. Final window state:`, {
          hasWindow: !!iframeWindow,
          hasBeemiDev: !!iframeWindow?.beemiDev,
          hasBeemi: !!iframeWindow?.beemi,
          hasManifest: !!iframeWindow?.__BEEMI_MANIFEST__,
          windowKeys: iframeWindow ? Object.keys(iframeWindow).filter(k => k.includes('beemi') || k.includes('BEEMI')) : []
        });
        clearInterval(checkInterval);
        reject(new Error('Emulator bridge timeout'));
      }
    }, 100);
  });
}

export function isBridgeReady(iframe: HTMLIFrameElement): boolean {
  const iframeWindow = iframe.contentWindow as any;
  return !!(iframeWindow?.beemiDev && iframeWindow?.beemi);
}

